import { ROLE } from "@/providers/AuthorizationProvider/types";

/**
 * Roles that receive national event/section bypass on the backend via
 * `Backoffice::User#nationally_scoped?` (same helper set as
 * `Membership::Event.visible_to` and `#manageable_sections`):
 * global_admin, hq_staff, membership_services_staff, career_services_staff,
 * recruiting_staff, player_engagement_staff, education_admin, finance_staff.
 *
 * Asymmetry vs nearby admin/staff pairs is intentional and matches backend:
 * - no `education_staff` (only `education_admin`)
 * - no `finance_admin` (only `finance_staff`)
 * - no `membership_services_admin` / `career_services_admin` (staff helpers only)
 *
 * When dual-roled with section staff, these roles keep national UX.
 */
export const NATIONAL_SECTION_BYPASS_ROLES: readonly ROLE[] = [
  ROLE.GLOBAL_ADMIN,
  ROLE.HQ_STAFF,
  ROLE.MEMBERSHIP_SERVICES_STAFF,
  ROLE.CAREER_SERVICES_STAFF,
  ROLE.RECRUITING_STAFF,
  ROLE.PLAYER_ENGAGEMENT_STAFF,
  ROLE.EDUCATION_ADMIN,
  ROLE.FINANCE_STAFF
];

type RoleChecker = {
  loaded: boolean;
  hasRole: (role: ROLE) => boolean;
};

export function hasNationalSectionBypassRole({ loaded, hasRole }: RoleChecker): boolean {
  return loaded && NATIONAL_SECTION_BYPASS_ROLES.some((role) => hasRole(role));
}

/**
 * True when the user is section-scoped for event / activity-type UX, or when
 * capabilities have not loaded yet (fail closed so national UX does not flash).
 * National-bypass roles win over an additional section_staff assignment.
 * Shared by event page clamps and `useActivityTypePermissions` so dual-role
 * behavior matches backend EventTypesQueries / CreateActivityTypeParticipation.
 */
export function isSectionScopedForEvents({ loaded, hasRole }: RoleChecker): boolean {
  if (!loaded) {
    return true;
  }

  if (hasNationalSectionBypassRole({ loaded, hasRole })) {
    return false;
  }

  return hasRole(ROLE.SECTION_STAFF) || hasRole(ROLE.SECTION_ADMIN);
}
